// src/api/pipeline.js
// 사전 생성된 script 로 영상 파이프라인의 미디어→음성→(크레딧)→렌더 단계를 실행한다.
// CLAUDE.md "7. 영상 생성 파이프라인" 의 4~7단계를 그대로 옮긴 재사용 헬퍼.
// Create.jsx 의 단계별 로직과 동일하게 유지할 것 (drift 시 두 곳을 함께 고친다).
//
// 호출 전제: 호출자가 이미 (1) 대본 생성 (2) videos/{videoId} 문서 생성을 마쳤다.
// 반환: { jobId } — 렌더는 webhook 으로 비동기 완료.

import { api } from './citizencast.js';
import { ANCHOR_VOICES } from '../utils/anchorVoices.js';

export async function renderShort({
  videoId,
  uid,
  script,
  country,
  targetLanguage = null,
  emotionTone = 'neutral',
  plan = 'tier3',
  logoUrl = null,
  forceWatermark = false,
  skipCredit = false,
  articleTitle = '',
}) {
  // 4) 배경 미디어 — 씬별 검색 우선, AI/Pexels 폴백 (Create.jsx 와 동일 병렬 전략)
  const articleSubject = (articleTitle || script.title || '')
    .split(/\s+/)
    .slice(0, 4)
    .join(' ');

  const queries = script.scenes.map((s) => {
    const q = (s.searchQuery || '').trim();
    return q || articleSubject || '';
  });

  const searchP = api.searchImages(queries, country, articleSubject).catch(() => ({ images: [] }));
  const aiP = api.generateImage(script.scenes, country, []).catch(() => ({ images: [] }));
  const pexelsP = api.fetchMedia(script.scenes, country, targetLanguage).catch(() => ({ media: [] }));

  const [searchRes, aiRes, pexelsRes] = await Promise.all([searchP, aiP, pexelsP]);
  const searchUrls = (searchRes.images || []).map((m) => m.url);
  const aiBySc = Object.fromEntries((aiRes.images || []).map((m) => [m.sceneNumber, m]));
  const pexelsBySc = Object.fromEntries((pexelsRes.media || []).map((m) => [m.sceneNumber, m]));

  const media = script.scenes.map((s, i) => {
    if (searchUrls[i]) return { sceneNumber: s.sceneNumber, type: 'photo', url: searchUrls[i] };
    const aiUrl = aiBySc[s.sceneNumber]?.url;
    if (aiUrl) return { sceneNumber: s.sceneNumber, type: 'photo', url: aiUrl };
    const pexels = pexelsBySc[s.sceneNumber];
    if (pexels?.url) return pexels;
    return { sceneNumber: s.sceneNumber, type: 'photo', url: null };
  });

  // 5) 음성 합성 — target 언어 우선, emotionTone 별 음성 ID
  const voiceCountry = targetLanguage || country;
  const voiceId = ANCHOR_VOICES[voiceCountry]?.[emotionTone] || ANCHOR_VOICES[voiceCountry]?.neutral;
  const voiceRes = await api.synthesizeVoice({
    scenes: script.scenes,
    voiceId,
    emotionTone,
    videoId,
    uid,
    language: voiceCountry, // TTS-SAFE 강제 변환 분기용 (KR/JP 만 정규식 적용)
  });

  // 6) 크레딧 차감 (admin 자동 생성은 skipCredit 으로 건너뜀)
  if (!skipCredit) {
    await api.deductCredit(uid, videoId);
  }

  // 7) 렌더
  const renderRes = await api.renderVideo({
    videoId,
    script,
    media,
    audioUrl: voiceRes.audioUrl,
    audioUrls: voiceRes.audioUrls,
    audioDurationSec: voiceRes.audioDurationSec,
    sceneDurations: voiceRes.sceneDurations,
    plan,
    country,
    logoUrl,
    forceWatermark,
  });

  return { jobId: renderRes.jobId };
}
